//CRN_20260508 - Riskline shared helpers (risk-level CSS, category icons, geojson conversion, validation, HTML sanitisation)
//CRN_20260513 - https://riskline.readme.io/reference/apis-overview

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { eRisklineCategory, eRisklineRiskLevel } from '../../enums';
import { ExceptionsService } from '../utelize/general/exceptions-service';
import { Injectable } from '@angular/core';
import { RisklineGeoJsonFeatureCollectionDTO, RisklineGeoJsonFeatureDTO } from '../../models/system/riskline/riskline-geojson.dto';
import { RisklineRiskLevelDTO } from '../../models/system/riskline/riskline-risk-level.dto';

@Injectable({ providedIn: 'root' })
export class RisklineSharedService {

  private static readonly TRUSTED_HTML_CACHE_MAX: number=1000;

  private readonly trustedHtmlCache: Map<string, SafeHtml>=new Map<string, SafeHtml>();

  constructor(
    private domSanitizer     : DomSanitizer,
    private exceptionsService: ExceptionsService
  ) { }

  //#region Grid sorting

  public getSortIcon(sortColumn: string, column: string, sortDescending: boolean): string {
    try {
      if (sortColumn!=column)
        return 'ri-arrow-up-down-line text-muted opacity-50';

      return sortDescending ? 'ri-arrow-down-line' : 'ri-arrow-up-line';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'ri-arrow-up-down-line text-muted opacity-50';
    }
  }

  public compareText(a: string | null | undefined, b: string | null | undefined): number {
    try {
      return (a ?? '').localeCompare((b ?? ''), undefined, { sensitivity: 'base' });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  //#endregion

  //#region Risk level

  public getRiskLevelBadgeClass(riskLevelId: number | string | undefined): string {
    try {
      const id=this.parseRiskLevelId(riskLevelId);

      switch (id) {
        case eRisklineRiskLevel.Low:
          return 'bg-success-subtle text-success';
        case eRisklineRiskLevel.Moderate:
          return 'bg-info-subtle text-info';
        case eRisklineRiskLevel.Medium:
          return 'bg-warning-subtle text-warning';
        case eRisklineRiskLevel.High:
          return 'bg-danger-subtle text-danger';
        case eRisklineRiskLevel.Extreme:
          return 'bg-dark-subtle text-dark';
        default:
          return 'bg-light text-muted';
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'bg-light text-muted';
    }
  }

  public getRiskLevelBadgeStyle(riskLevelId: number | string | undefined): { [key: string]: string; } {
    try {
      const hex=this.getRiskLevelHexColour(riskLevelId);

      return {
        'background-color': hex + '26',
        'color'           : hex
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { 'background-color': '#878a9926', 'color': '#878a99' };
    }
  }

  public getRiskLevelId(riskLevelId: number | string | undefined): number {
    try {
      return this.parseRiskLevelId(riskLevelId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public getRiskLevelLabelWithLevel(riskLevelId: number | string | undefined): string {
    try {
      const label=this.getRiskLevelLabel(riskLevelId);
      const id   =this.parseRiskLevelId(riskLevelId);

      if (id<1 || id>5 || label=='Unknown')
        return label;

      return label + ' - ' + id;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'Unknown';
    }
  }

  public getRiskLevelHexColour(riskLevelId: number | string | undefined): string {
    try {
      const id=this.parseRiskLevelId(riskLevelId);

      switch (id) {
        case eRisklineRiskLevel.Low:
          return '#0ab39c';
        case eRisklineRiskLevel.Moderate:
          return '#299cdb';
        case eRisklineRiskLevel.Medium:
          return '#f7b84b';
        case eRisklineRiskLevel.High:
          return '#f06548';
        case eRisklineRiskLevel.Extreme:
          return '#CC0000';
        default:
          return '#878a99';
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '#878a99';
    }
  }

  public getRiskLevelLabel(riskLevel: RisklineRiskLevelDTO | number | string | undefined): string {
    try {
      if (riskLevel==null)
        return 'Unknown';

      if (typeof riskLevel==='object' && riskLevel?.name)
        return riskLevel.name;

      const id=this.parseRiskLevelId(riskLevel as number | string);

      switch (id) {
        case eRisklineRiskLevel.Low:
          return 'Low';
        case eRisklineRiskLevel.Moderate:
          return 'Moderate';
        case eRisklineRiskLevel.Medium:
          return 'Medium';
        case eRisklineRiskLevel.High:
          return 'High';
        case eRisklineRiskLevel.Extreme:
          return 'Extreme';
        default:
          return 'Unknown';
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'Unknown';
    }
  }

  //#endregion

  //#region Category

  public getCategoryIconClass(categoryId: number | string | undefined): string {
    try {
      const id=this.parseCategoryId(categoryId);

      switch (id) {
        case eRisklineCategory.PoliticalDevelopments:
          return 'ri-government-line';
        case eRisklineCategory.ConflictAndTerrorism:
          return 'ri-shield-cross-line';
        case eRisklineCategory.DemonstrationAndUnrest:
          return 'ri-megaphone-line';
        case eRisklineCategory.Crime:
          return 'ri-handcuffs-line';
        case eRisklineCategory.NaturalAndEnvironmental:
          return 'ri-tornado-line';
        case eRisklineCategory.HealthAndMedical:
          return 'ri-virus-line';
        case eRisklineCategory.TravelSafetyAndDisruptions:
          return 'ri-flight-takeoff-line';
        default:
          return 'ri-information-line';
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'ri-information-line';
    }
  }

  public getCategoryLabel(categoryId: number | string | undefined): string {
    try {
      const id=this.parseCategoryId(categoryId);

      switch (id) {
        case eRisklineCategory.PoliticalDevelopments:
          return 'Political Developments';
        case eRisklineCategory.ConflictAndTerrorism:
          return 'Conflict and Terrorism';
        case eRisklineCategory.DemonstrationAndUnrest:
          return 'Demonstration and Unrest';
        case eRisklineCategory.Crime:
          return 'Crime';
        case eRisklineCategory.NaturalAndEnvironmental:
          return 'Natural and Environmental';
        case eRisklineCategory.HealthAndMedical:
          return 'Health and Medical';
        case eRisklineCategory.TravelSafetyAndDisruptions:
          return 'Travel Safety and Disruptions';
        default:
          return 'Unknown';
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'Unknown';
    }
  }

  //#endregion

  //#region GeoJSON

  public extractFeatures(featureCollection: RisklineGeoJsonFeatureCollectionDTO | undefined | null): RisklineGeoJsonFeatureDTO[] {
    try {
      if (!featureCollection || !featureCollection.features)
        return [];

      return featureCollection.features;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public toLeafletLatLng(feature: RisklineGeoJsonFeatureDTO): [number, number] {
    try {
      //CRN_20260508 - Riskline GeoJSON is [lng, lat]; Leaflet wants [lat, lng]
      const [lng, lat]=feature.geometry.coordinates;

      return [lat, lng];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [0, 0];
    }
  }

  //#endregion

  //#region Validation

  public isFutureDate(value: string): boolean {
    try {
      if (!value)
        return false;

      const today=new Date();

      today.setHours(0, 0, 0, 0);

      const target=new Date(value);

      return !isNaN(target.getTime()) && target>=today;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public validateDateRange(from: string, to: string): { isValid: boolean; message: string; } {
    try {
      if (!from || !to)
        return { isValid: false, message: 'Both From and To dates are required' };

      const fromDate=new Date(from);
      const toDate  =new Date(to);

      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime()))
        return { isValid: false, message: 'Dates must be valid YYYY-MM-DD values' };

      if (!this.isFutureDate(from))
        return { isValid: false, message: 'From date must be today or later' };

      if (toDate<fromDate)
        return { isValid: false, message: 'To date must be on or after From date' };

      return { isValid: true, message: '' };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { isValid: false, message: 'Date validation failed' };
    }
  }

  public validateIsoCountryCode(isoCode: string | undefined): boolean {
    try {
      if (!isoCode)
        return false;

      return /^[A-Za-z]{2}$/.test(isoCode);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  //#endregion

  //#region HTML sanitisation

  public trustHtml(html: string | undefined | null): SafeHtml {
    try {
      if (html==null)
        return '';

      let safeHtml: SafeHtml | undefined=this.trustedHtmlCache.get(html);

      if (safeHtml===undefined) {
        if (this.trustedHtmlCache.size>=RisklineSharedService.TRUSTED_HTML_CACHE_MAX)
          this.trustedHtmlCache.clear();

        safeHtml=this.domSanitizer.bypassSecurityTrustHtml(html);
        this.trustedHtmlCache.set(html, safeHtml);
      }

      return safeHtml;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }
  //#endregion

  //#region Private helpers

  private parseCategoryId(value: number | string | undefined): number {
    try {
      if (value==null)
        return 0;

      if (typeof value==='number')
        return value;

      const parsed=parseInt(value, 10);

      return isNaN(parsed) ? 0 : parsed;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  private parseRiskLevelId(value: number | string | undefined): number {
    try {
      if (value==null)
        return 0;

      if (typeof value==='number')
        return value;

      const parsed=parseInt(value, 10);

      return isNaN(parsed) ? 0 : parsed;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  //#endregion
}
